/**
 * ControllerPage — Mobile-Web-Controller (über QR-Code geöffnet).
 *
 * Zwei virtuelle Steuerkreuze (Touch-Joysticks):
 *  - links  → Translation (vor/zurück, links/rechts)
 *  - rechts → Rotation (yaw/pitch)
 *
 * Eingabe wird mit ~30 Hz an /ws/controller/<sessionId> gestreamt.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Maximize2, Minimize2,
  Box, Table2, LayoutDashboard, Columns3, FileText, Files,
  ClipboardCheck, StickyNote, Upload, Leaf, Focus, Grape,
} from 'lucide-react'
import { API_URL } from '../../lib/apiBase'

// Nav-Sections, die der Mobile-Controller als Toggle anzeigt.
// Reihenfolge spiegelt die Desktop-Sidebar wider.
const NAV_SECTIONS: Array<{ id: string; label: string; Icon: any }> = [
  { id: 'gebaeude',          label: 'Gebäude',     Icon: Box },
  { id: 'raumbuch',          label: 'Raumbuch',    Icon: Table2 },
  { id: 'whiteboard',        label: 'Whiteboard',  Icon: LayoutDashboard },
  { id: 'board',             label: 'Board',       Icon: Columns3 },
  { id: 'reports',           label: 'Reports',     Icon: FileText },
  { id: 'docs',              label: 'Docs',        Icon: Files },
  { id: 'audits',            label: 'Audits',      Icon: ClipboardCheck },
  { id: 'notes',             label: 'Notes',       Icon: StickyNote },
  { id: 'pointcloud-upload', label: 'Upload',      Icon: Upload },
  { id: 'emmissionssenken',  label: 'CO₂',         Icon: Leaf },
  { id: 'scene',             label: 'Scene',       Icon: Focus },
  { id: 'punktwolke',        label: 'Render',      Icon: Grape },
]

interface Vec2 { x: number; y: number }
const ZERO: Vec2 = { x: 0, y: 0 }

function wsUrlFor(sessionId: string): string {
  const base = API_URL || window.location.origin
  const wsBase = base.replace(/^http/i, 'ws')
  // siehe GameControllerSession: /ws/yjs/-Prefix wegen nginx-Routing
  return `${wsBase}/ws/yjs/controller/${encodeURIComponent(sessionId)}`
}

export function ControllerPage() {
  const sessionId = useMemo(() => new URLSearchParams(window.location.search).get('session') || '', [])
  const [paired, setPaired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const leftRef = useRef<Vec2>(ZERO)
  const rightRef = useRef<Vec2>(ZERO)
  const extraRef = useRef<Vec2>(ZERO)
  const wsRef = useRef<WebSocket | null>(null)
  const [activeSection, setActiveSection] = useState<string | null>(null)

  // ── Page-Lifecycle: Viewport sperren, Scroll/Pinch deaktivieren ─────────
  useEffect(() => {
    // 1) Viewport-Meta-Tag forcen: kein Zoom, no scale
    const viewport = document.querySelector('meta[name="viewport"]')
    const prevContent = viewport?.getAttribute('content') ?? null
    if (viewport) {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover'
      )
    }

    // 2) html + body: kein Scrollen, aber touch-action 'manipulation' lassen
    // (verhindert iOS-Doppeltap-Zoom, erhält aber Click-Events). Vorher hatten
    // wir 'none' gesetzt + globalen touchend-preventDefault, das unterdrückte
    // alle Tap-Clicks (Button reagierte nicht mehr).
    const html = document.documentElement
    const body = document.body
    const prevStyles = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlTouchAction: (html.style as any).touchAction,
      bodyTouchAction: (body.style as any).touchAction,
      bodyOverscroll: (body.style as any).overscrollBehavior,
    }
    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'
    ;(html.style as any).touchAction = 'manipulation'
    ;(body.style as any).touchAction = 'manipulation'
    ;(body.style as any).overscrollBehavior = 'none'

    // 3) iOS Safari: Pinch hart blocken; einzelnes Touch-Move bleibt frei,
    // damit die Joysticks dragbar bleiben und Clicks normal feuern.
    const preventGesture = (e: Event) => { e.preventDefault() }
    const preventTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 1) e.preventDefault()
    }
    document.addEventListener('gesturestart', preventGesture, { passive: false })
    document.addEventListener('gesturechange', preventGesture, { passive: false })
    document.addEventListener('gestureend', preventGesture, { passive: false })
    document.addEventListener('touchmove', preventTouchMove, { passive: false })

    return () => {
      if (viewport && prevContent !== null) viewport.setAttribute('content', prevContent)
      html.style.overflow = prevStyles.htmlOverflow
      body.style.overflow = prevStyles.bodyOverflow
      ;(html.style as any).touchAction = prevStyles.htmlTouchAction
      ;(body.style as any).touchAction = prevStyles.bodyTouchAction
      ;(body.style as any).overscrollBehavior = prevStyles.bodyOverscroll
      document.removeEventListener('gesturestart', preventGesture)
      document.removeEventListener('gesturechange', preventGesture)
      document.removeEventListener('gestureend', preventGesture)
      document.removeEventListener('touchmove', preventTouchMove)
    }
  }, [])

  // Fullscreen-State synchron halten
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
      } else {
        await document.documentElement.requestFullscreen()
        // Auf Mobile: Bildschirm-Orientierung wenn möglich ins Querformat sperren
        try { await (screen.orientation as any)?.lock?.('landscape') } catch { /* ignore */ }
      }
    } catch (e) {
      console.warn('[ctrl mobile] fullscreen request failed:', e)
    }
  }

  // WS-Verbindung
  useEffect(() => {
    if (!sessionId) { setError('Keine Session-ID in der URL.'); return }

    const ws = new WebSocket(wsUrlFor(sessionId))
    wsRef.current = ws
    ws.onopen = () => {
      try { ws.send(JSON.stringify({ type: 'hello', role: 'controller' })) } catch { /* ignore */ }
    }
    ws.onmessage = (ev) => {
      let msg: any
      try { msg = JSON.parse(String(ev.data)) } catch { return }
      if (msg?.type === 'session-state') {
        if (Array.isArray(msg.peers) && msg.peers.includes('viewer')) setPaired(true)
      }
      if (msg?.type === 'peer-connected' && msg.role === 'viewer') setPaired(true)
      if (msg?.type === 'peer-disconnected' && msg.role === 'viewer') setPaired(false)
      if (msg?.type === 'nav-state') setActiveSection(msg.activeSection ?? null)
    }
    ws.onerror = () => setError('Verbindungsfehler')
    ws.onclose = () => setPaired(false)
    return () => { try { ws.close(1000, 'unmount') } catch { /* ignore */ } }
  }, [sessionId])

  // Input-Loop: alle 33ms (~30Hz) den aktuellen Stick-State senden.
  // Wir senden auch genau ein Zero-Frame nach dem Loslassen, damit der
  // Viewer die Bewegung stoppt (Idle-Frames danach werden gespart).
  useEffect(() => {
    let lastLog = 0
    let lastWasZero = true
    const id = window.setInterval(() => {
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      const l = leftRef.current, r = rightRef.current, e = extraRef.current
      const isZero = l.x === 0 && l.y === 0 && r.x === 0 && r.y === 0 && e.x === 0 && e.y === 0
      if (isZero && lastWasZero) return  // schon ruhig, nichts zu senden
      const payload = { type: 'input', lx: l.x, ly: l.y, rx: r.x, ry: r.y, ex: e.x, ey: e.y }
      try { ws.send(JSON.stringify(payload)) } catch { /* ignore */ }
      lastWasZero = isZero
      const now = performance.now()
      if (now - lastLog > 300) {
        lastLog = now
        console.log('[ctrl mobile] send', payload)
      }
    }, 33)
    return () => window.clearInterval(id)
  }, [])

  // Beim Verlassen: einmalig Null-Input senden, damit der Viewer aufhört zu fahren
  useEffect(() => {
    return () => {
      const ws = wsRef.current
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'input', lx: 0, ly: 0, rx: 0, ry: 0, ex: 0, ey: 0 })) } catch { /* ignore */ }
      }
    }
  }, [])

  if (error) {
    return (
      <div style={fullCentered}>
        <p style={{ color: '#dc2626', fontSize: 16 }}>{error}</p>
      </div>
    )
  }

  const sendNav = (sectionId: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    try { ws.send(JSON.stringify({ type: 'nav', section: sectionId })) } catch { /* ignore */ }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: '#0b0f17', color: '#e2e8f0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 32px', fontFamily: 'system-ui, sans-serif',
        touchAction: 'none', userSelect: 'none', overflow: 'hidden',
      }}
    >
      {/* ── Icon-Leiste (Toggle für Desktop-Menü) ────────────────────────── */}
      <div
        style={{
          position: 'absolute', top: 8, left: 0, right: 0,
          display: 'flex', justifyContent: 'center', gap: 4,
          padding: '4px 8px', flexWrap: 'wrap',
        }}
      >
        {NAV_SECTIONS.map(({ id, label, Icon }) => {
          const isActive = activeSection === id
          return (
            <button
              key={id}
              onClick={() => sendNav(id)}
              title={label}
              style={{
                width: 38, height: 38, borderRadius: 8,
                background: isActive ? '#1e293b' : 'transparent',
                color: isActive ? '#fafafa' : '#94a3b8',
                border: isActive ? '1px solid #475569' : '1px solid transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', touchAction: 'manipulation',
              }}
            >
              <Icon size={18} strokeWidth={1.5} />
            </button>
          )
        })}
      </div>
      {/* Linker Bereich: großer Bewegen-Stick + kleiner Höhen-/Seitwärts-Stick rechts-unten */}
      <div style={{ position: 'relative' }}>
        <Joystick label="Bewegen" onChange={(v) => { leftRef.current = v }} />
        <div style={{ position: 'absolute', left: 180, top: 150 }}>
          <Joystick size={100} label="Höhe/Seite" onChange={(v) => { extraRef.current = v }} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 18, fontWeight: 600 }}>orto Controller</span>
        <span style={{ fontSize: 12, color: paired ? '#22c55e' : '#94a3b8' }}>
          {paired ? '● verbunden' : '○ wartet auf Viewer'}
        </span>
        <button
          onClick={toggleFullscreen}
          style={{
            marginTop: 10, padding: '8px 14px', borderRadius: 999,
            border: '1px solid #475569', background: 'transparent',
            color: '#e2e8f0', fontSize: 12,
            display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            touchAction: 'manipulation',
          }}
        >
          {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          {isFullscreen ? 'Verlassen' : 'Vollbild'}
        </button>
        <span style={{ fontSize: 10, color: '#475569', marginTop: 6 }}>Querformat empfohlen</span>
      </div>

      <Joystick label="Drehen" onChange={(v) => { rightRef.current = v }} />
    </div>
  )
}

const fullCentered: React.CSSProperties = {
  position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#0b0f17', color: '#fff', fontFamily: 'system-ui, sans-serif',
}

// ─── Joystick-Component ─────────────────────────────────────────────────────

function Joystick({ label, onChange, size = 200 }: { label: string; onChange: (v: Vec2) => void; size?: number }) {
  const SIZE = size
  const KNOB = Math.round(size * 0.36)
  const RADIUS = (SIZE - KNOB) / 2
  const baseRef = useRef<HTMLDivElement | null>(null)
  const [knob, setKnob] = useState<Vec2>(ZERO)
  const activePointerRef = useRef<number | null>(null)
  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  const setFromClientXY = (clientX: number, clientY: number) => {
    const base = baseRef.current
    if (!base) return
    const rect = base.getBoundingClientRect()
    const cx = rect.left + rect.width / 2
    const cy = rect.top + rect.height / 2
    let dx = clientX - cx
    let dy = clientY - cy
    const dist = Math.hypot(dx, dy)
    if (dist > RADIUS) {
      dx = (dx / dist) * RADIUS
      dy = (dy / dist) * RADIUS
    }
    const norm: Vec2 = { x: dx / RADIUS, y: -dy / RADIUS } // y invertiert: oben = +1
    setKnob({ x: dx, y: dy })
    onChangeRef.current(norm)
  }

  const release = () => {
    setKnob(ZERO)
    onChangeRef.current(ZERO)
    activePointerRef.current = null
  }

  // Native Touch- und Pointer-Listener am DOM-Element registrieren mit
  // passive:false, damit preventDefault zuverlässig greift (iOS Safari).
  useEffect(() => {
    const el = baseRef.current
    if (!el) return

    // ── Pointer-Events (modern, Maus + Touch + Pen) ─────────────────────
    const onPointerDown = (e: PointerEvent) => {
      e.preventDefault()
      try { (el as any).setPointerCapture?.(e.pointerId) } catch { /* ignore */ }
      activePointerRef.current = e.pointerId
      setFromClientXY(e.clientX, e.clientY)
    }
    const onPointerMove = (e: PointerEvent) => {
      if (activePointerRef.current !== e.pointerId) return
      e.preventDefault()
      setFromClientXY(e.clientX, e.clientY)
    }
    const onPointerUp = (e: PointerEvent) => {
      if (activePointerRef.current !== e.pointerId) return
      e.preventDefault()
      release()
    }
    el.addEventListener('pointerdown', onPointerDown, { passive: false })
    el.addEventListener('pointermove', onPointerMove, { passive: false })
    el.addEventListener('pointerup', onPointerUp, { passive: false })
    el.addEventListener('pointercancel', onPointerUp, { passive: false })

    // ── Touch-Fallback (für sehr alte iOS-Versionen ohne Pointer-API) ──
    const onTouchStart = (e: TouchEvent) => {
      if (activePointerRef.current !== null) return
      const t = e.changedTouches[0]
      if (!t) return
      e.preventDefault()
      activePointerRef.current = t.identifier
      setFromClientXY(t.clientX, t.clientY)
    }
    const onTouchMove = (e: TouchEvent) => {
      const id = activePointerRef.current
      if (id === null) return
      for (let i = 0; i < e.changedTouches.length; i++) {
        const t = e.changedTouches[i]
        if (t.identifier === id) { e.preventDefault(); setFromClientXY(t.clientX, t.clientY); return }
      }
    }
    const onTouchEnd = (e: TouchEvent) => {
      const id = activePointerRef.current
      if (id === null) return
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === id) { e.preventDefault(); release(); return }
      }
    }
    el.addEventListener('touchstart', onTouchStart, { passive: false })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: false })
    el.addEventListener('touchcancel', onTouchEnd, { passive: false })

    return () => {
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', onPointerUp)
      el.removeEventListener('pointercancel', onPointerUp)
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div
        ref={baseRef}
        style={{
          position: 'relative',
          width: SIZE, height: SIZE,
          borderRadius: '50%',
          background: 'radial-gradient(circle, #1e293b 0%, #0f172a 70%)',
          border: '2px solid #334155',
          boxShadow: 'inset 0 4px 18px rgba(0,0,0,0.5)',
          touchAction: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: SIZE / 2 - KNOB / 2 + knob.x,
            top: SIZE / 2 - KNOB / 2 + knob.y,
            width: KNOB, height: KNOB,
            borderRadius: '50%',
            background: 'linear-gradient(180deg, #e2e8f0, #94a3b8)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.6)',
            transition: activePointerRef.current === null ? 'left 0.15s, top 0.15s' : 'none',
            pointerEvents: 'none',
          }}
        />
      </div>
      <span style={{ fontSize: 12, color: '#94a3b8' }}>{label}</span>
    </div>
  )
}
